import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskAssignmentApi } from './TaskAssignmentApi';
import { TaskAssignmentDTO } from './TaskAssignmentApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faTrash, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

export const TaskAssignmentList: React.FC = () => {
  const [taskAssignments, setTaskAssignments] = useState<TaskAssignmentDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await TaskAssignmentApi.search({});
      setTaskAssignments(response.content || []);
    } catch (error) {
      console.error('Error fetching task assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/task-assignments/edit/${id}`);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        await TaskAssignmentApi.delete(id);
        fetchAssignments();
      } catch (error) {
        console.error('Error deleting assignment:', error);
      }
    }
  };

  const handleCreate = () => {
    navigate('/task-assignments/create');
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Task Assignment List</h2>
        <div className="flex gap-2">
          <button onClick={handleBack} className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded flex items-center">
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
            Back
          </button>
          <button onClick={handleCreate} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded flex items-center">
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Create Assignment
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded shadow">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left px-4 py-2 border">User Name</th>
                <th className="text-left px-4 py-2 border">Role</th>
                <th className="text-left px-4 py-2 border">Status</th>
                <th className="text-left px-4 py-2 border">Assigned At</th>
                <th className="text-left px-4 py-2 border">Assigned By</th>
                <th className="text-center px-4 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {taskAssignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 border">{assignment.userName}</td>
                  <td className="px-4 py-2 border">{assignment.role}</td>
                  <td className="px-4 py-2 border">{assignment.status}</td>
                  <td className="px-4 py-2 border">{assignment.assignedAt?.toString().slice(0, 10)}</td>
                  <td className="px-4 py-2 border">{assignment.assignedBy}</td>
                  <td className="px-4 py-2 border text-center space-x-2">
                    <button
                      onClick={() => handleEdit(assignment.id!)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() => handleDelete(assignment.id!)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
              {taskAssignments.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    No task assignments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};